import { ref, shallowRef, type Ref } from 'vue';
import { useRouter } from 'vue-router';

interface Section {
  id: string;
  title: string;
  ref: Ref<HTMLElement | null>;
}

type LinkId = 'catalog' | 'about' | 'machines' | 'feedback';

interface Link {
  id: LinkId;
  title: string;
}

// Базовый список ссылок, доступный всегда
const links: Link[] = [
  { id: 'catalog', title: 'Каталог' },
  { id: 'about', title: 'О нас' },
  { id: 'machines', title: 'Автоматы' },
  { id: 'feedback', title: 'Возврат товара' }
];

const sections = shallowRef<Section[]>([]);
const activeSection = ref<string | null>(null);

// Константы для настройки поведения
const HEADER_OFFSET = 120; // Отступ от верха при скролле (подстройте под высоту вашего хедера)
const INTERSECTION_THRESHOLD = [0, 0.2, 0.4, 0.6]; // Несколько порогов для более плавного определения видимости

// Храним информацию о видимости каждой секции
const sectionVisibility = ref(new Map());

// Флаг, предотвращающий множественные вызовы в одном кадре
let ticking = false;

function updateActiveSection(entries: IntersectionObserverEntry[]) {
  let maxVisibility = 0;
  let mostVisibleSection: string | null = null;

  entries.forEach(entry => {
    const section = sections.value.find(s => s.ref.value === entry.target);
    if (section) {
      sectionVisibility.value.set(section.id, entry.intersectionRatio);
    }
  });

  sectionVisibility.value.forEach((visibility, sectionId) => {
    if (visibility > maxVisibility) {
      maxVisibility = visibility;
      mostVisibleSection = sectionId;
    }
  });

  // Активируем секцию, если она достаточно видима и отличается от текущей
  if (maxVisibility > 0.4 && activeSection.value !== mostVisibleSection) {
    activeSection.value = mostVisibleSection;
  }

  ticking = false;
}

const observerCallback = (entries: IntersectionObserverEntry[]) => {
  // Запускаем обновление секции через requestAnimationFrame, если оно еще не запланировано
  if (!ticking) {
    ticking = true;
    requestAnimationFrame(() => updateActiveSection(entries));
  }
};

const observer = new IntersectionObserver(observerCallback, {
  threshold: INTERSECTION_THRESHOLD // Используем несколько порогов
});

export function useSectionTracking() {
  const router = useRouter();

  function setupSectionsTracking(
    sectionsRefs: {
      id: string;
      elementRef: Ref<HTMLElement | null>;
    }[]
  ) {
    sectionsRefs.forEach(sectionRef => {
      const link = links.find(l => l.id === sectionRef.id);

      if (!link) return;

      sections.value.push({
        id: link.id,
        title: link.title,
        ref: sectionRef.elementRef
      });
    });

    sections.value.forEach(section => {
      if (section.ref.value) {
        observer.observe(section.ref.value);
        sectionVisibility.value.set(section.id, 0);
      }
    });
  }

  function teardownSectionsTracking() {
    observer.disconnect();

    sections.value = [];

    activeSection.value = null;
  }

  const scrollToSection = (linkId: string) => {
    const section = sections.value.find(s => s.id === linkId);
    if (!section?.ref.value) return;

    const elementPosition = section.ref.value.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - HEADER_OFFSET;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    router.push({ query: { ...router.currentRoute.value.query, to: linkId } });
  };

  function getLinkParams(id: LinkId) {
    return {
      to: id
    };
  }

  function getLinkPath(id: LinkId, params?: Record<string, string>) {
    return {
      path: '/',
      query: { to: id, ...params }
    };
  }

  return {
    links,
    sections,
    activeSection,
    scrollToSection,
    INTERSECTION_THRESHOLD,
    setupSectionsTracking,
    teardownSectionsTracking,
    getLinkPath,
    getLinkParams
  };
}
